import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DotloomError, formatAngle, formatDuration, formatLength, LENGTH_UNITS, parseAngle, parseDuration, parseLength, } from '@dotloomjs/sdk';
import { useEffect, useId, useRef, useState } from 'react';
import { useDocument, useEditor, useEditorState, useEntityInfo, usePluginTypes } from '../context.js';
import { useT } from '../i18n.js';
import { Icon } from './icons.js';
const ANGLE_PARAMS = new Set(['start', 'sweep', 'rotation']);
/** Dimension of a parameter: from the plugin definition, or by name for built-ins. */
function dimOf(name, def) {
    const p = def?.props?.[name.split('.')[0] ?? name];
    if (p && p.type === 'number')
        return p.dim ?? 'scalar';
    if (p && p.type === 'point')
        return 'length';
    return ANGLE_PARAMS.has(name) ? 'angle' : 'length';
}
export function formatValue(v, dim, unit) {
    if (dim === 'length')
        return formatLength(v, unit, undefined, false);
    if (dim === 'angle')
        return formatAngle(v).replace('°', '');
    if (dim === 'time')
        return formatDuration(v);
    return String(Math.round(v * 1e6) / 1e6);
}
export function parseValue(text, dim, unit) {
    if (dim === 'length')
        return parseLength(text, unit);
    if (dim === 'angle')
        return parseAngle(text);
    if (dim === 'time')
        return parseDuration(text);
    const v = Number(text.trim().replace(',', '.'));
    return Number.isFinite(v) ? v : null;
}
function unitLabel(dim, unit) {
    return dim === 'length' ? LENGTH_UNITS[unit].symbol : dim === 'angle' ? '°' : '';
}
function paramRef(entity, name, builtin) {
    return builtin ? { entity, geom: name } : { entity, prop: name };
}
function sameRef(a, b) {
    return a.entity === b.entity && JSON.stringify(a) === JSON.stringify(b);
}
function describe(e, t, entity, param) {
    if (e instanceof DotloomError) {
        const d = e.details;
        const near = d?.failure?.nearest?.find((n) => n.entity === entity && n.param === param);
        const labels = d?.failure?.diagnostics?.flatMap((x) => x.labels) ?? [];
        const msg = labels.length > 0 ? `${t('error.conflict')} ${labels.join('; ')}` : e.message;
        return near ? { message: msg, nearest: near.feasible } : { message: msg };
    }
    return { message: e instanceof Error ? e.message : String(e) };
}
/** A numeric parameter with units, lock toggle and inline solver feedback. */
function NumberField(props) {
    const { core } = useEditor();
    const t = useT();
    const id = useId();
    const shown = formatValue(props.value, props.dim, props.unit);
    const [text, setText] = useState(shown);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    // Follow engine updates, except while the user is typing: a value that arrives
    // then (late entity info, a concurrent solve) would replace the typed text.
    // Enter/blur submits (engine values show again afterwards), Escape discards.
    const editing = useRef(false);
    useEffect(() => {
        if (!editing.current)
            setText(shown);
    }, [shown]);
    const commit = async (value) => {
        setBusy(true);
        try {
            await core.applyOrThrow({
                label: `Set ${props.label}`,
                commands: [{ op: 'setParams', values: [{ entity: props.entity, param: props.name, value }], mode: 'exact' }],
            });
            setError(null);
        }
        catch (e) {
            setError(describe(e, t, props.entity, props.name));
        }
        finally {
            setBusy(false);
        }
    };
    const submit = () => {
        editing.current = false;
        if (text.trim() === shown) {
            setError(null);
            return;
        }
        const v = parseValue(text, props.dim, props.unit);
        if (v === null) {
            setError({ message: t('inspector.invalid') });
            return;
        }
        void commit(v);
    };
    const toggleLock = () => {
        const tx = props.locked
            ? { label: 'Unlock', commands: [{ op: 'removeConstraint', id: props.locked.id }] }
            : {
                label: 'Lock',
                commands: [
                    {
                        op: 'addConstraint',
                        constraint: {
                            rule: { kind: 'fix', param: paramRef(props.entity, props.name, props.builtin), value: props.value },
                            label: `${props.label} locked`,
                        },
                    },
                ],
            };
        void core.apply(tx);
    };
    const sym = unitLabel(props.dim, props.unit);
    return (_jsxs("div", { className: "dl-field", children: [_jsx("label", { htmlFor: id, title: props.name, children: props.label }), _jsxs("div", { className: "dl-row", children: [_jsx("input", { id: id, className: "dl-input", inputMode: "decimal", value: text, readOnly: props.readOnly, "aria-invalid": error !== null, "aria-describedby": error ? `${id}-err` : undefined, "aria-busy": busy, onChange: (e) => {
                            editing.current = true;
                            setText(e.target.value);
                        }, onBlur: submit, onKeyDown: (e) => {
                            if (e.key === 'Enter')
                                submit();
                            if (e.key === 'Escape') {
                                editing.current = false;
                                setText(shown);
                                setError(null);
                            }
                        } }), _jsx("span", { className: "dl-muted", style: { minWidth: 18 }, children: sym })] }), _jsx("button", { type: "button", className: "dl-btn dl-icon-btn", "aria-pressed": props.locked !== null, "aria-label": `${props.locked ? t('inspector.unlock') : t('inspector.lock')}: ${props.label}`, title: props.locked ? t('inspector.unlock') : t('inspector.lock'), disabled: props.readOnly, onClick: toggleLock, children: _jsx(Icon, { name: props.locked ? 'lock' : 'unlock', size: 16 }) }), error && (_jsxs("div", { className: "dl-field-error", id: `${id}-err`, role: "alert", children: [error.message, error.nearest !== undefined && (_jsx("div", { children: _jsx("button", { type: "button", className: "dl-btn", onClick: () => {
                                const n = error.nearest;
                                setText(formatValue(n, props.dim, props.unit));
                                void commit(n);
                            }, children: t('inspector.nearest', {
                                value: `${formatValue(error.nearest, props.dim, props.unit)} ${sym}`.trim(),
                            }) }) }))] }))] }));
}
function OtherField(props) {
    const { core } = useEditor();
    const id = useId();
    const set = (v) => {
        void core.apply({
            label: `Set ${props.name}`,
            commands: [{ op: 'updateEntity', id: props.entity, patch: { props: { [props.name]: v } } }],
        });
    };
    const label = props.def.label ?? props.name;
    let control = null;
    if (props.def.type === 'enum') {
        control = (_jsx("select", { id: id, className: "dl-select", disabled: props.readOnly, value: typeof props.value === 'string' ? props.value : (props.def.default ?? ''), onChange: (e) => set(e.target.value), children: props.def.values.map((v) => (_jsx("option", { value: v, children: v }, v))) }));
    }
    else if (props.def.type === 'bool') {
        control = (_jsx("input", { id: id, type: "checkbox", disabled: props.readOnly, checked: props.value === true, onChange: (e) => set(e.target.checked) }));
    }
    else if (props.def.type === 'text') {
        control = (_jsx("input", { id: id, className: "dl-input", disabled: props.readOnly, defaultValue: typeof props.value === 'string' ? props.value : '', onBlur: (e) => set(e.currentTarget.value) }));
    }
    else {
        return null;
    }
    return (_jsxs("div", { className: "dl-field", children: [_jsx("label", { htmlFor: id, children: label }), control, _jsx("span", {})] }));
}
function readOnlyText(info, t) {
    const r = info.readOnly;
    if (!r)
        return null;
    return t(`inspector.readOnly.${r.reason}`, { type: r.type_id });
}
function EntityInspector(props) {
    const { core } = useEditor();
    const t = useT();
    const info = useEntityInfo(props.id);
    const doc = useDocument();
    const types = usePluginTypes();
    if (!info || !doc)
        return _jsx("p", { className: "dl-muted", children: "\u2026" });
    const e = info.entity;
    const unit = doc.settings?.displayUnit ?? 'millimetre';
    const builtin = e.type.startsWith('dotloom.');
    const def = types.get(e.type);
    const roText = readOnlyText(info, t);
    const readOnly = roText !== null || e.locked === true;
    const fixes = doc.constraints.filter((c) => c.rule.kind === 'fix');
    const lockOf = (name) => fixes.find((c) => c.rule.kind === 'fix' && sameRef(c.rule.param, paramRef(e.id, name, builtin))) ?? null;
    const numeric = def
        ? Object.keys(info.params).filter((n) => {
            const root = n.split('.')[0] ?? n;
            return def.props?.[root] !== undefined;
        })
        : Object.keys(info.params);
    const others = def ? Object.entries(def.props ?? {}).filter(([, p]) => p.type !== 'number' && p.type !== 'point') : [];
    const update = (patch, label) => {
        void core.apply({ label, commands: [{ op: 'updateEntity', id: e.id, patch }] });
    };
    return (_jsxs("div", { children: [_jsxs("div", { className: "dl-row", children: [_jsx("strong", { children: def?.label ?? e.type.replace(/^dotloom\./, '') }), _jsxs("span", { className: "dl-muted", children: ["#", e.id] })] }), roText && _jsx("div", { className: "dl-banner", children: roText }), info.error && _jsx("div", { className: "dl-banner dl-error", children: info.error }), _jsx("p", { className: "dl-muted", style: { margin: '4px 0' }, children: t('inspector.unitHint', { unit: LENGTH_UNITS[unit].symbol }) }), _jsxs("div", { className: "dl-field", children: [_jsx("label", { htmlFor: `dl-name-${e.id}`, children: t('inspector.name') }), _jsx("input", { id: `dl-name-${e.id}`, className: "dl-input", defaultValue: e.name ?? '', disabled: roText !== null, onBlur: (ev) => {
                            if (ev.currentTarget.value !== (e.name ?? ''))
                                update({ name: ev.currentTarget.value }, 'Rename');
                        } }, `name-${e.id}-${e.name ?? ''}`), _jsx("span", {})] }), _jsxs("div", { className: "dl-field", children: [_jsx("label", { htmlFor: `dl-layer-${e.id}`, children: t('inspector.layer') }), _jsx("select", { id: `dl-layer-${e.id}`, className: "dl-select", value: e.layer, disabled: roText !== null, onChange: (ev) => update({ layer: Number(ev.target.value) }, 'Change layer'), children: doc.layers.map((l) => (_jsx("option", { value: l.id, children: l.name }, l.id))) }), _jsx("span", {})] }), _jsxs("div", { className: "dl-row", children: [_jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: e.locked === true, onChange: (ev) => update({ locked: ev.target.checked }, 'Lock') }), ' ', t('inspector.locked')] }), _jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: e.hidden === true, onChange: (ev) => update({ hidden: ev.target.checked }, 'Hide') }), ' ', t('inspector.hidden')] })] }), numeric.map((n) => {
                const v = info.params[n];
                const root = n.split('.')[0] ?? n;
                const pd = def?.props?.[root];
                const base = pd && 'label' in pd && pd.label ? pd.label : n;
                const label = n.includes('.') && pd ? `${base} ${n.slice(root.length + 1)}` : base;
                return (_jsx(NumberField, { entity: e.id, name: n, label: label, value: v, dim: dimOf(n, def), unit: unit, readOnly: readOnly, locked: lockOf(n), builtin: builtin }, `${e.id}-${n}`));
            }), others.map(([name, pd]) => (_jsx(OtherField, { entity: e.id, name: name, def: pd, value: e.props?.[name], readOnly: readOnly }, name))), info.measured !== null && _jsxs("p", { children: ["= ", formatLength(info.measured, unit)] })] }));
}
function DocumentSettings() {
    const { core, viewport } = useEditor();
    const t = useT();
    const doc = useDocument();
    if (!doc)
        return null;
    const unit = doc.settings?.displayUnit ?? 'millimetre';
    const set = (patch) => {
        void core.apply({ label: 'Settings', commands: [{ op: 'setSettings', patch }] });
    };
    return (_jsxs("div", { children: [_jsx("p", { className: "dl-muted", children: t('inspector.none') }), _jsx("h3", { style: { fontSize: 13, margin: '8px 0 4px' }, children: t('panel.document') }), _jsxs("div", { className: "dl-field", children: [_jsx("label", { htmlFor: "dl-doc-title", children: t('document.title') }), _jsx("input", { id: "dl-doc-title", className: "dl-input", defaultValue: doc.meta?.title ?? '', onBlur: (e) => {
                            if (e.currentTarget.value !== (doc.meta?.title ?? ''))
                                set({ title: e.currentTarget.value });
                        } }, doc.meta?.title ?? ''), _jsx("span", {})] }), _jsxs("div", { className: "dl-field", children: [_jsx("label", { htmlFor: "dl-doc-unit", children: t('document.unit') }), _jsx("select", { id: "dl-doc-unit", className: "dl-select", value: unit, onChange: (e) => set({ displayUnit: e.target.value }), children: Object.keys(LENGTH_UNITS).map((u) => (_jsx("option", { value: u, children: LENGTH_UNITS[u].symbol }, u))) }), _jsx("span", {})] }), _jsxs("div", { className: "dl-field", children: [_jsx("label", { htmlFor: "dl-doc-grid", children: t('document.grid') }), _jsx("input", { id: "dl-doc-grid", className: "dl-input", defaultValue: formatLength(viewport.grid.spacing, unit, undefined, false), onBlur: (e) => {
                            const v = parseLength(e.currentTarget.value, unit);
                            if (v !== null && v > 0) {
                                viewport.setGrid({ spacing: v });
                                set({ gridSpacing: v });
                            }
                        } }, `${viewport.grid.spacing}-${unit}`), _jsx("span", { className: "dl-muted", children: LENGTH_UNITS[unit].symbol })] })] }));
}
function MultiInspector(props) {
    const { core } = useEditor();
    const t = useT();
    return (_jsxs("div", { children: [_jsx("p", { children: t('inspector.many', { n: props.ids.length }) }), _jsxs("div", { className: "dl-row", children: [_jsxs("button", { type: "button", className: "dl-btn", onClick: () => void core.deleteSelection(), children: [_jsx(Icon, { name: "trash", size: 16 }), " ", t('inspector.delete')] }), _jsx("button", { type: "button", className: "dl-btn", onClick: () => void core.run('group'), children: t('inspector.group') })] })] }));
}
/** Properties of the selection, or document settings when nothing is selected. */
export function Inspector() {
    const t = useT();
    const selection = useEditorState((s) => s.selection);
    return (_jsxs("section", { className: "dl-panel", "aria-labelledby": "dl-inspector-h", children: [_jsx("h2", { id: "dl-inspector-h", children: t('panel.inspector') }), selection.length === 0 && _jsx(DocumentSettings, {}), selection.length === 1 && selection[0] !== undefined && _jsx(EntityInspector, { id: selection[0] }), selection.length > 1 && _jsx(MultiInspector, { ids: selection })] }));
}
//# sourceMappingURL=Inspector.js.map